import { useState } from 'react';
import { Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  PayrollDatePreset, 
  PAYROLL_PRESET_OPTIONS,
  getPayrollDateRange,
  PayrollDateRange
} from '@/utils/payrollDateUtils';

interface PayrollDateRangeFilterProps {
  timezone: string;
  onRangeChange: (range: PayrollDateRange, compareToLY: boolean) => void;
  showCompareToggle?: boolean;
}

export function PayrollDateRangeFilter({ 
  timezone, 
  onRangeChange,
  showCompareToggle = true 
}: PayrollDateRangeFilterProps) {
  const [preset, setPreset] = useState<PayrollDatePreset>('wtd');
  const [customStart, setCustomStart] = useState<Date | undefined>(undefined);
  const [customEnd, setCustomEnd] = useState<Date | undefined>(undefined);
  const [compareToLY, setCompareToLY] = useState(false);

  const handlePresetChange = (value: PayrollDatePreset) => {
    setPreset(value);
    
    if (value !== 'custom') {
      const range = getPayrollDateRange(value, timezone);
      onRangeChange(range, compareToLY);
    }
  };

  const handleCustomDateChange = () => {
    if (customStart && customEnd) {
      const range = getPayrollDateRange('custom', timezone, customStart, customEnd);
      onRangeChange(range, compareToLY);
    }
  };

  const handleCompareToggle = (checked: boolean) => {
    setCompareToLY(checked);
    
    if (preset !== 'custom') {
      const range = getPayrollDateRange(preset, timezone);
      onRangeChange(range, checked);
    } else if (customStart && customEnd) {
      const range = getPayrollDateRange('custom', timezone, customStart, customEnd);
      onRangeChange(range, checked);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1.5">
        <Label className="text-sm">Date Range</Label>
        <Select value={preset} onValueChange={handlePresetChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select range" />
          </SelectTrigger>
          <SelectContent>
            {PAYROLL_PRESET_OPTIONS.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {preset === 'custom' && (
        <>
          <div className="space-y-1.5">
            <Label className="text-sm">Start Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                  <Calendar className="mr-2 h-4 w-4" />
                  {customStart ? format(customStart, 'MMM d, yyyy') : 'Start'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <CalendarComponent
                  mode="single"
                  selected={customStart}
                  onSelect={(date) => {
                    setCustomStart(date);
                    if (date && customEnd) handleCustomDateChange();
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">End Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                  <Calendar className="mr-2 h-4 w-4" />
                  {customEnd ? format(customEnd, 'MMM d, yyyy') : 'End'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <CalendarComponent
                  mode="single"
                  selected={customEnd}
                  onSelect={(date) => {
                    setCustomEnd(date);
                    if (customStart && date) handleCustomDateChange();
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <Button 
            variant="secondary" 
            size="sm"
            onClick={handleCustomDateChange}
            disabled={!customStart || !customEnd}
          >
            Apply
          </Button>
        </>
      )}

      {showCompareToggle && (
        <div className="flex items-center space-x-2">
          <Switch
            id="compare-ly"
            checked={compareToLY}
            onCheckedChange={handleCompareToggle}
          />
          <Label htmlFor="compare-ly" className="text-sm cursor-pointer">
            Compare to Last Year
          </Label>
        </div>
      )}
    </div>
  );
}
